/**
 * ShareStreamModal — Share live stream link.
 *
 * Props:
 *   isOpen, onClose, streamId, streamerName, viewerCount
 *   isHost  — true for the broadcaster, false for viewers
 *   api     — axios instance (only needed for in-app share actions)
 *   userId  — current user id (for in-app share)
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { toast } from 'sonner';
import {
  X,
  Copy,
  Check,
  Share2,
  MessageCircle,
  Users,
  UserCheck,
  Send,
  Search,
  Loader2,
} from 'lucide-react';

const ShareStreamModal = ({
  isOpen,
  onClose,
  streamId,
  streamerName,
  viewerCount = 0,
  isHost = false,
  api,
  userId,
}) => {
  const [copied, setCopied] = useState(false);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Share the OG-tag share endpoint (prod) so WhatsApp/iMessage scrapers show
  // the broadcaster's cover + title; humans are instantly redirected to /watch.
  const shareUrl = `https://gemslive.tv/api/s/${streamId}`;
  const shareText = `Watch ${streamerName} play live on GEMS Live! Join now: ${shareUrl}`;

  useEffect(() => {
    if (!isOpen) {
      setCopied(false);
      setShowUserPicker(false);
      setSelectedUsers([]);
      setSearchQuery('');
      setUsers([]);
    }
  }, [isOpen]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied!');
      trackShare('copy_link');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const shareWhatsApp = () => {
    trackShare('whatsapp');
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
  };

  const shareSMS = () => {
    trackShare('sms');
    window.open(`sms:?body=${encodeURIComponent(shareText)}`, '_blank');
  };

  const tryNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `${streamerName} is live on GEMS!`, text: shareText, url: shareUrl });
        trackShare('native_share');
      } catch { /* user cancelled */ }
    }
  };

  const trackShare = (method) => {
    axios.post(`${API_BASE_URL}/api/streams/preview-analytics`, {
      stream_id: streamId,
      event_type: 'stream_shared',
      metadata: { method, is_host: isHost },
    }).catch(() => {});
    // Round 6: external shares (copy link / OS share sheet) count toward the
    // one-time Get Started reward — server enforces the once-only.
    if (api && ['copy_link', 'native_share', 'whatsapp', 'sms'].includes(method)) {
      api.post(`/streams/${streamId}/share-external`).catch(() => {});
    }
  };

  // ── Host-only: share to followers / friends ──
  const shareToAllFollowers = async () => {
    if (!api) return;
    setSending(true);
    try {
      await api.post(`/notifications/share-stream`, {
        stream_id: streamId,
        target: 'followers',
      });
      toast.success('Shared with all followers!');
      trackShare('share_followers');
    } catch {
      toast.error('Failed to share');
    } finally {
      setSending(false);
    }
  };

  const shareToAllFriends = async () => {
    if (!api) return;
    setSending(true);
    try {
      await api.post(`/notifications/share-stream`, {
        stream_id: streamId,
        target: 'friends',
      });
      toast.success('Shared with all friends!');
      trackShare('share_friends');
    } catch {
      toast.error('Failed to share');
    } finally {
      setSending(false);
    }
  };

  // Load followers for "Select Specific Users"
  const loadFollowers = async () => {
    if (!api) return;
    setLoadingUsers(true);
    setShowUserPicker(true);
    try {
      // First try to get followers from the user's follower list
      const res = await api.get(`/users/me/followers`);
      let followerList = res.data?.followers || res.data || [];
      
      // Sort by most recent interaction/follow date
      followerList = followerList.sort((a, b) => {
        // Priority: last_interaction > followed_at > created_at
        const aDate = new Date(a.last_interaction || a.followed_at || a.created_at || 0);
        const bDate = new Date(b.last_interaction || b.followed_at || b.created_at || 0);
        return bDate - aDate; // Most recent first
      });
      
      setUsers(followerList);
    } catch (err) {
      console.error('Failed to load followers:', err);
      // Fallback: try to get friends if followers endpoint fails
      try {
        const friendsRes = await api.get(`/users/me/friends`);
        setUsers(friendsRes.data?.friends || friendsRes.data || []);
      } catch {
        setUsers([]);
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const toggleUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(u => u !== userId) : [...prev, userId]
    );
  };

  const shareToSelectedUsers = async () => {
    if (!api || selectedUsers.length === 0) return;
    setSending(true);
    try {
      await api.post(`/notifications/share-stream`, {
        stream_id: streamId,
        target: 'selected',
        user_ids: selectedUsers,
      });
      toast.success(`Shared with ${selectedUsers.length} user(s)!`);
      trackShare('share_selected');
      setShowUserPicker(false);
      setSelectedUsers([]);
    } catch {
      toast.error('Failed to share');
    } finally {
      setSending(false);
    }
  };

  // Filter users by name, handle, OR ID (GEMS ID)
  const filteredUsers = users.filter(u => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;
    return (
      (u.name || '').toLowerCase().includes(query) ||
      (u.handle || '').toLowerCase().includes(query) ||
      (u.id || '').toLowerCase().includes(query) ||
      (u.gems_id || '').toLowerCase().includes(query)
    );
  });

  if (!isOpen) return null;

  return (
    // z-[9999] ensures modal is above all other elements including stream controls
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" 
      style={{ zIndex: 9999 }}
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-gray-900 rounded-t-3xl sm:rounded-2xl border border-gray-700 overflow-hidden max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="share-stream-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Share2 className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-bold text-white">Share Live</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors" data-testid="share-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stream info */}
        <div className="px-5 py-3 flex items-center gap-3 border-b border-gray-800/50 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-white">{streamerName}</span>
          </div>
          <span className="text-sm text-gray-500">{viewerCount} watching</span>
        </div>

        {/* Scrollable content */}
        <div className="p-5 space-y-3 overflow-y-auto flex-1">
          {/* Native share (mobile) */}
          {typeof navigator !== 'undefined' && navigator.share && (
            <button
              onClick={tryNativeShare}
              className="w-full flex items-center gap-3 px-4 py-3 bg-emerald-600 hover:bg-emerald-500 rounded-xl text-white font-medium transition-colors"
              data-testid="share-native-btn"
            >
              <Share2 className="w-5 h-5" />
              Share
            </button>
          )}

          {/* Copy Link */}
          <button
            onClick={copyLink}
            className="w-full flex items-center gap-3 px-4 py-3 bg-gray-800 hover:bg-gray-700 rounded-xl text-white transition-colors"
            data-testid="share-copy-link-btn"
          >
            {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5 text-gray-400" />}
            <span>{copied ? 'Copied!' : 'Copy Link'}</span>
          </button>

          {/* WhatsApp */}
          <button
            onClick={shareWhatsApp}
            className="w-full flex items-center gap-3 px-4 py-3 bg-gray-800 hover:bg-gray-700 rounded-xl text-white transition-colors"
            data-testid="share-whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5 text-green-400" />
            <span>WhatsApp</span>
          </button>

          {/* SMS */}
          <button
            onClick={shareSMS}
            className="w-full flex items-center gap-3 px-4 py-3 bg-gray-800 hover:bg-gray-700 rounded-xl text-white transition-colors"
            data-testid="share-sms-btn"
          >
            <Send className="w-5 h-5 text-blue-400" />
            <span>Text Message</span>
          </button>

          {/* ── Host-only in-app share options ── */}
          {isHost && (
            <>
              <div className="pt-2 pb-1">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">In-App Share</p>
              </div>

              <button
                onClick={shareToAllFollowers}
                disabled={sending}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-800 hover:bg-gray-700 rounded-xl text-white transition-colors disabled:opacity-50"
                data-testid="share-followers-btn"
              >
                <Users className="w-5 h-5 text-purple-400" />
                <span>Share to All Followers</span>
              </button>

              <button
                onClick={shareToAllFriends}
                disabled={sending}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-800 hover:bg-gray-700 rounded-xl text-white transition-colors disabled:opacity-50"
                data-testid="share-friends-btn"
              >
                <UserCheck className="w-5 h-5 text-amber-400" />
                <span>Share to Friends</span>
              </button>

              <button
                onClick={loadFollowers}
                disabled={sending || loadingUsers}
                className="w-full flex items-center gap-3 px-4 py-3 bg-gray-800 hover:bg-gray-700 rounded-xl text-white transition-colors disabled:opacity-50"
                data-testid="share-select-users-btn"
              >
                {loadingUsers ? (
                  <Loader2 className="w-5 h-5 text-teal-400 animate-spin" />
                ) : (
                  <Search className="w-5 h-5 text-teal-400" />
                )}
                <span>Select Specific Users</span>
              </button>

              {/* User picker (for selecting specific followers) */}
              {showUserPicker && (
                <div className="border border-gray-700 rounded-xl p-3 space-y-2 bg-gray-800/50">
                  {/* Search input */}
                  <input
                    type="text"
                    placeholder="Search by username or GEMS ID..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-900 rounded-lg border border-gray-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 placeholder-gray-500"
                    data-testid="share-user-search"
                  />
                  
                  {/* User list */}
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {loadingUsers ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
                      </div>
                    ) : filteredUsers.length === 0 ? (
                      <div className="text-center py-6">
                        <Users className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                        <p className="text-gray-500 text-sm">
                          {users.length === 0 ? 'No followers yet' : 'No users match your search'}
                        </p>
                      </div>
                    ) : (
                      filteredUsers.map(u => (
                        <button
                          key={u.id}
                          onClick={() => toggleUser(u.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                            selectedUsers.includes(u.id) ? 'bg-emerald-600/30 border border-emerald-500' : 'hover:bg-gray-700'
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold overflow-hidden flex-shrink-0">
                            {u.picture ? <img src={u.picture} alt="" className="w-full h-full object-cover" /> : (u.name || '?')[0]}
                          </div>
                          <div className="text-left flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{u.name}</p>
                            {u.handle && <p className="text-gray-500 text-xs truncate">@{u.handle}</p>}
                          </div>
                          {selectedUsers.includes(u.id) && <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                        </button>
                      ))
                    )}
                  </div>
                  
                  {/* Send button */}
                  {selectedUsers.length > 0 && (
                    <button
                      onClick={shareToSelectedUsers}
                      disabled={sending}
                      className="w-full mt-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="share-send-selected-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Share with {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareStreamModal;
